import { Controller } from "@hotwired/stimulus"

const COPIED_CLASS = "copied";
const COPY_ICON = "icon-lasso-copy";
const CHECK_ICON = "icon-lasso-check";
const RESTING_LABEL = "Copy";
const CONFIRMED_LABEL = "Copied";
const RESET_DELAY = 2000;

// https://stackoverflow.com/a/65996386
function writeToClipboard(textToCopy) {
  // The clipboard API is exposed only in a secure context, so anything served over plain http -
  // and every feature spec - takes the textarea branch.
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(textToCopy);
  }

  // The textarea has to take focus to be selected; hand it back afterwards so keyboard and screen
  // reader users stay on the button they pressed
  const previouslyFocused = document.activeElement;
  const textArea = document.createElement("textarea");
  textArea.value = textToCopy;
  textArea.style.position = "fixed";
  textArea.style.left = "-999999px";
  textArea.style.top = "-999999px";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  return new Promise((resolve, reject) => {
    document.execCommand("copy") ? resolve() : reject();
    textArea.remove();
    previouslyFocused?.focus();
  });
}

export default class extends Controller {
  // `status` (optional): selector of a live region to announce the copy in. The relabel below is
  // visual only when the button is named by aria-label.
  // `icon` (optional): the button's resting icon, swapped for a check while confirming.
  static get values() { return { selector: String, status: String, icon: { type: String, default: COPY_ICON } }; }

  // The reset is the only thing this controller holds. A button torn down mid-confirmation should
  // keep whatever state it was left in rather than be rewritten by a controller that is gone.
  disconnect() {
    clearTimeout(this.resetTimeoutId);
    this.resetTimeoutId = null;
  }

  copy() {
    writeToClipboard(this.sourceText()).
      then(() => this.confirm()).
      catch(() => console.warn("writing to clipboard failed"));
  }

  // jQuery's .text() concatenates every element the selector matched and yields "" for none.
  sourceText() {
    return Array.from(document.querySelectorAll(this.selectorValue)).
      map(element => element.textContent).
      join("").
      trim();
  }

  confirm() {
    this.announce("Link copied to clipboard");
    this.relabel(CONFIRMED_LABEL);
    this.element.classList.add(COPIED_CLASS);
    this.swapIcon(this.iconValue, CHECK_ICON);

    this.resetTimeoutId = setTimeout(() => {
      this.relabel(RESTING_LABEL);
      this.swapIcon(CHECK_ICON, this.iconValue);
      this.element.classList.remove(COPIED_CLASS);
    }, RESET_DELAY);
  }

  // Cleared first so copying again re-announces the same message
  announce(message) {
    const region = this.hasStatusValue && document.querySelector(this.statusValue);
    if (!region) { return; }

    region.textContent = "";
    setTimeout(() => { region.textContent = message; }, 50);
  }

  // Several buttons render their caption as bare text rather than a span, and there the relabel is
  // a no-op - only the icon and the class report the copy.
  relabel(text) {
    this.element.querySelectorAll("span").forEach(span => { span.textContent = text; });
  }

  swapIcon(from, to) {
    this.element.querySelectorAll(`.${from}`).forEach(icon => icon.classList.replace(from, to));
  }
}
